import {
  type BrandKit,
  DealValueRange,
  SalesCycleLength
} from "@/lib/api/schema";
import {
  OldIdealCustomers1,
  Under250Business,
  IdealCustomers1,
  IdealCustomers2
} from "@/constants/AgentBrandInfo";

const formatSegments = (oldData: string[], segments: string[]): string[] => {
  const hasUnder250PeopleData = OldIdealCustomers1.some((v) =>
    oldData.includes(v)
  );

  const newSegments = [...segments];

  if (hasUnder250PeopleData && !segments.includes(Under250Business)) {
    newSegments.push(Under250Business);
  }

  // Only add items from oldData if this is the initial migration (segments array is empty)
  // Don't re-add items that user has explicitly unchecked
  if (segments.length === 0) {
    oldData.forEach((v) => {
      if (IdealCustomers1.includes(v) && !newSegments.includes(v)) {
        newSegments.push(v);
      }
    });
  }

  // Only add custom data from oldData if this is the initial migration (segments array is empty)
  if (segments.length === 0) {
    const customDataList = oldData.filter(
      (v) => !IdealCustomers1.includes(v) && !IdealCustomers2.includes(v)
    );

    if (customDataList[0] && !newSegments.includes(customDataList[0])) {
      newSegments.push(customDataList[0]);
    }
  }

  return newSegments;
};

const formatVerticals = (oldData: string[], verticals: string[]) => {
  const newVerticals = [...verticals];

  // Only add items from oldData if this is the initial migration (verticals array is empty)
  // Don't re-add items that user has explicitly unchecked
  if (verticals.length === 0) {
    const customDataList = oldData.filter(
      (v) => !IdealCustomers1.includes(v) && !IdealCustomers2.includes(v)
    );

    oldData.forEach((v) => {
      if (IdealCustomers2.includes(v) && !newVerticals.includes(v)) {
        newVerticals.push(v);
      }
    });

    if (customDataList[1] && !newVerticals.includes(customDataList[1])) {
      newVerticals.push(customDataList[1]);
    }
  }

  return newVerticals;
};

/**
 * @Note:
 * `segments` and `verticals` are newly added data structures. This method is intended to maintain compatibility with the previous `ideal_customer_profile` data.
 * @param brandKit
 * @returns
 */
export const formatBrandKit = (brandKit: BrandKit) => {
  const {
    ideal_customer_profile = [],
    segments = [],
    verticals = []
  } = brandKit.sales_config || {};

  return {
    ...brandKit,
    sales_config: {
      ...brandKit.sales_config,
      segments: formatSegments(ideal_customer_profile, segments),
      verticals: formatVerticals(ideal_customer_profile, verticals)
    }
  };
};

export const getSalesCycleLengthOptions = () => [
  { value: SalesCycleLength.Monthly, label: "Immediate" },
  { value: SalesCycleLength["1_4_weeks"], label: "1 - 4 weeks" },
  { value: SalesCycleLength["1_month"], label: "1 month" },
  { value: SalesCycleLength["1_3_months"], label: "1 - 3 months" },
  { value: SalesCycleLength["3_plus_months"], label: "3+ months" }
];

export const getDealValueRangeOptions = () => [
  { value: DealValueRange.Under1K, label: "Under $1K" },
  { value: DealValueRange["1k_5k"], label: "$1K - $5K" },
  { value: DealValueRange["5k_25k"], label: "$5K - $25K" },
  { value: DealValueRange["100k_plus"], label: "$100K+" }
];
